import React, { useEffect, useState } from 'react';
import CustomDropdown from '../../../components/ui/CustomDropdown';
import AuthPrimaryButton from '../../../components/ui/AuthPrimaryButton';
import {
    modalBody,
    modalCloseBtn,
    modalField,
    modalLabel,
    modalStack,
    modalSubtitle,
    modalTitle,
} from '../../../components/ui/modalDesign';
import { toast } from 'react-toastify';
import UserService from '../../../services/user.service';
import { X } from 'lucide-react';
import {useSelector} from "react-redux";
import {RootState} from "../../../redux/rootReducer";
import {filterDaysOfTheWeek, restructureTimeData} from "../../../utils/Tools";
import {MIN_AGE_FOR_REG, filterOpenCohorts, resolvePackageForChild} from "../../../utils/constants";

export interface ChildRegProps {
}

const initialValues = {
}

interface Props {
    handleNext: () => void;
    setChildInfo: (_arg1: any, _arg2: any, _cohortId: number) => void;
    closeModalOnOutsideClick: (data: boolean) => void;
    closeRegTab: () => void;
}


const inputFieldStyle = modalField;
const labelStyle = modalLabel;

//global time storage
let staticTime: any = []
let cohortKeep: any = []

export default function RegResumptionModal({ handleNext, setChildInfo, closeModalOnOutsideClick, closeRegTab }: Props) {
    // const userInfo: any = useSelector((state:RootState) => state.auth.userInfo);
    const userInfoTarget: any = useSelector((state: RootState) => state.user.resumeChildReg);
    const userService = new UserService();

    const [ formData, setFormData ] = useState(initialValues);
    const [ dayTime, setDayTime ] = useState<any>([]);
    const [ dayArr, setDayArr ] = useState<{ name: string; value: number; }[] | []>([]);
    const [ selectedDay, setSelectedDay ] = useState<{ name: string, value: number } | null>(null);
    const [ timeArr, setTimeArr ] = useState<{ name: string; value: number; }[] | []>([]);
    const [ cohortId, setCohortId ] = useState(0);
    const [ cohortArr, setCohortArr ] = useState<{ name: string; value: number; }[] | []>([]);
    const  [ selectedTime, setSelectedTime ] = useState<{ name: string, value: number } | null>(null);
    const [ loading, setLoading ] = useState(false);
    //new states
    const [packages, setPackages] = useState<any>([])
    const [selectedPackage, setSelectedPackage] = useState<any>()
    const [selectedSubPackage, setSelectedSubPackageId] = useState<any>(null)

    // load all available days and their corresponding time for BE
    const getDayTime = async() => {
        try{
            const response = await userService.getDayTime();
            if(!response.status){
                toast.error(response.message)
                return;
            }
            setDayTime(response.data);
            staticTime = response.data
            if (response?.data?.length > 0) {
                // extracted dayText and day values and saved them as 'name' and 'value' respectively for ease of use in the custom dropdown component
                setDayArr(filterDaysOfTheWeek(response?.data))
            }
            // dispatch(setDayTimeInfo(response.data))

            const responsePkg = await userService.getAllPackages()
            if (!responsePkg.status) {
                toast.error(response.message)
                return;
            }
            setPackages(responsePkg.data)

            const responseCohort = await userService.getCohort()
            if(!responseCohort.status){
                toast.error(responseCohort.message)
                return;
            }
            cohortKeep = responseCohort.data
            // Filter cohorts immediately based on current age if available
            if (userInfoTarget?.age >= MIN_AGE_FOR_REG) {
                setCohortArr(filterOpenCohorts(cohortKeep))
            } else {
                // If age not set yet, show all cohorts (will be filtered when age is set)
                setCohortArr(responseCohort.data);
            }
        }catch(err: any){
            toast.error(err?.message);
        }
        return false;
    };


    const handleSubmit = async(e: any) => {
        e.preventDefault();
        if(!selectedDay){
            toast.error('Date is required!');
            return;
        }
        if(!selectedTime){
            toast.error('Time is required!');
            return;
        }
        if(!cohortId){
            toast.error('Cohort is required!');
            return;
        }
        setChildInfo(selectedDay.value, selectedTime.value, cohortId);
        handleNext();
    };


    const keepModalOpen = () => {
        // if all these are unfilled or empty then closing modal by clicking outside is possible
        if(selectedTime === null && selectedDay === null){
            closeModalOnOutsideClick(true)
        }else{
            closeModalOnOutsideClick(false)
        }
    }


    useEffect(() => {
        getDayTime();
        keepModalOpen();
    }, []);

    useEffect(() => {
        keepModalOpen();
    }, [formData])

    useEffect(() => {
        if(selectedDay){
            const timeX = dayTime?.filter((item: any) => item.dayText === selectedDay.name);
            const tdx = timeX[0].times;
            // extracted timeText and time values and saved them as 'name' and 'value' respectively for ease of use in the custom dropdown component
            let arr = []
            for(let i=0; i < tdx.length; i++){
                let name = tdx[i].timeText;
                let value = tdx[i].time;
                let _dy = { name, value };
                arr.push(_dy);
            }
            setTimeArr(arr)
        }
    }, [ selectedDay ]);

    useEffect(() => {
        if(userInfoTarget?.age>=MIN_AGE_FOR_REG){
            const _age = userInfoTarget.age
            const pkg = resolvePackageForChild(packages, _age, 1)
            if(pkg){
                setSelectedPackage(pkg)
                localStorage.setItem("package", JSON.stringify(pkg))
                //offload remote times
                if(pkg?.timeGroup){
                    //this has a unique time
                    const remTimeData = restructureTimeData(pkg?.timeGroup?.times)
                    setDayArr(filterDaysOfTheWeek(remTimeData))
                    setDayTime(remTimeData)
                }else{
                    //populate normal time
                    setDayTime(staticTime)
                    setDayArr(filterDaysOfTheWeek(staticTime))
                }
            }else{
               if(packages.length>0){
                   toast.warn("Please try again next time. Program within the age range does not exist.")
               }
            }
        }
    }, [packages, userInfoTarget?.age]);

    // Filter cohorts based on age - separate effect to ensure it always runs
    useEffect(() => {
        if (userInfoTarget?.age >= MIN_AGE_FOR_REG && cohortKeep && cohortKeep.length > 0) {
            setCohortArr(filterOpenCohorts(cohortKeep))
        }
    }, [userInfoTarget?.age]);

    return (
        <form className={`relative ${modalBody}`} onSubmit={handleSubmit}>
            <button type="button" aria-label="Close" className={modalCloseBtn} onClick={closeRegTab}>
                <X size={18} />
            </button>

            <div className="mb-5 pr-8 text-center sm:mb-6">
                <h1 className={modalTitle}>Resume Registration</h1>
                <p className={modalSubtitle}>Pick lesson day, time, and cohort to continue enrollment.</p>
            </div>

            <div className={modalStack}>
                <div>
                    <label className={labelStyle}>Program</label>
                    <p className={`${inputFieldStyle} text-[#8b9aab]`}>
                        {selectedPackage?.title || 'Loading program…'}
                    </p>
                </div>

                <div>
                    <label className={labelStyle}>Lesson day</label>
                    <CustomDropdown
                        className={inputFieldStyle}
                        handleChange={(data: { name: string, value: number}) => setSelectedDay(data)}
                        data={dayArr}
                    />
                </div>

                <div>
                    <label className={labelStyle}>Lesson time</label>
                    <CustomDropdown
                        className={inputFieldStyle}
                        handleChange={(data: { name: string, value: number}) => setSelectedTime(data)}
                        data={timeArr}
                    />
                </div>

                <div>
                    <label className={labelStyle}>Choose cohort</label>
                    <select
                        className={inputFieldStyle}
                        onChange={(e: any) => setCohortId(Number(e.target.value))}
                    >
                        <option value={0}>Choose cohort</option>
                        {cohortArr && cohortArr.map((d: any, i: number) => (
                            <option key={i} value={d.id}>{d.title}</option>
                        ))}
                    </select>
                </div>

                <AuthPrimaryButton loading={loading}>Next</AuthPrimaryButton>
            </div>
        </form>
    )
}
